import { invariant } from "@multiweb/reactUtils";
import { ReduxCompatibleValue, TypedObject } from "@multiweb/types/react";
import {
    INVALID_CONTROL_VALUE_LOC_KEY,
    MAX_LENGTH_ERROR_LOC_KEY,
    MAX_VALUE_VALIDATION_ERROR_ARGS_LOC_KEY,
    MIN_VALUE_VALIDATION_ERROR_ARGS_LOC_KEY,
} from "../locKeys";
import { ControlReduxRawValue } from "../types";
import {
    CustomizableExplicitValidationFnKey,
    ExplicitValidationFnKey,
    ExplicitValidationFunction,
    IExplicitValidationError,
    IExplicitValidationFnDescription,
    ILocalizedExplicitValidationError,
} from "./types";
import {
    getTypedParamsForIsMaxLengthValidOrFail,
    getTypedParamsForIsMaxValueValidOrFail,
    getTypedParamsForIsMinValueValidOrFail,
    getTypedParamsForIsRegexValidOrFail,
} from "./utils";
import * as validationFunctions from "./validationFunctions";

/**
 * Tento soubor obsahuje validace spouštěné pouze na vyžádání.
 * Validuje se existující hodnota v reduxu a výsledkem validace je seznam validačních chyb - tj. důvodů, proč hodnota není validní.
 * Více viz `readme.md`.
 */

/**
 * Zvaliduje, zda zadaná řetězcová hodnota není delší, než maximální délka.
 *
 * @param value Hodnota k validaci. Musí to být řetězec.
 * @param args Obsahuje maximální délku, vůči níž se `value` validuje.
 * Spolu s `value` musí vyhovovat rozhraní {@link IValidateMaxLengthParams}.
 * @param locKey Lokalizační klíč, který se použije pro návratovou chybovou hlášku, pokud validace neprojde.
 * @return Vhodný {@link IExplicitValidationError}, pokud validace neprojde, jinak `null`.
 */
export const validateMaxLength: ExplicitValidationFunction = (value, args: TypedObject<ReduxCompatibleValue>, locKey: string) => {
    const typedParams = getTypedParamsForIsMaxLengthValidOrFail(value, args);
    return validationFunctions.isMaxLengthValid(typedParams) ? null : createValidationError(locKey, [String(typedParams.maxLength)]);
};

/**
 * Zvaliduje, zda zadaná řetězcová hodnota vyhovuje danému regulárnímu výrazu.
 *
 * @param value Hodnota k validaci. Musí to být řetězec.
 * @param args Obsahuje regulární výraz, vůči kterému se `value` validuje.
 * Spolu s `value` musí vyhovovat rozhraní {@link IValidateRegexParams}.
 * @param locKey Lokalizační klíč, který se použije pro návratovou chybovou hlášku, pokud validace neprojde.
 * @return Vhodný {@link IExplicitValidationError}, pokud validace neprojde, jinak `null`.
 */
export const validateRegex: ExplicitValidationFunction = (value, args: TypedObject<ReduxCompatibleValue>, locKey: string) => {
    const typedParams = getTypedParamsForIsRegexValidOrFail(value, args);
    return validationFunctions.isRegexValid(typedParams) ? null : createValidationError(locKey);
};

/**
 * Kontroluje, zda je zadaná hodnota větší nebo rovna nejmenší povolené hodnotě.
 * @param value Hodnota k validaci. Musí to být řetězec.
 * @param args Obsahuje příznak, zda se má hodnota validovat jako BigNumber a minimální povolenou hodnotu.
 * @param locKey Lokalizační klíč, který se použije pro návratovou chybovou hlášku, pokud validace neprojde.
 * @return Vhodný {@link IExplicitValidationError}, pokud validace neprojde, jinak `null`.
 */
export const validateMinValue: ExplicitValidationFunction = (value, args: TypedObject<ReduxCompatibleValue>, locKey: string) => {
    const typedParams = getTypedParamsForIsMinValueValidOrFail(value, args);
    const isValid = typedParams.isBigNumber
        ? validationFunctions.isMinValueValid(typedParams)
        : validationFunctions.isMinValueBigNumberValid(typedParams);
    return isValid ? null : createValidationError(locKey, [String(typedParams.minValue)]);
};

/**
 * Kontroluje, zda je zadaná hodnota menší nebo rovna největší povolené hodnotě.
 * @param value Hodnota k validaci. Musí to být řetězec.
 * @param args Obsahuje příznak, zda se má hodnota validovat jako BigNumber, a maximální povolenou hodnotu.
 * @param locKey Lokalizační klíč, který se použije pro návratovou chybovou hlášku, pokud validace neprojde.
 * @return Vhodný {@link IExplicitValidationError}, pokud validace neprojde, jinak `null`.
 */
export const validateMaxValue: ExplicitValidationFunction = (value, args: TypedObject<ReduxCompatibleValue>, locKey: string) => {
    const typedParams = getTypedParamsForIsMaxValueValidOrFail(value, args);
    const isValid = typedParams.isBigNumber
        ? validationFunctions.isMaxValueValid(typedParams)
        : validationFunctions.isMaxValueBigNumberValid(typedParams);
    return isValid ? null : createValidationError(locKey, [String(typedParams.maxValue)]);
};

/**
 * Mapuje klíče na validační funkce. Určeno pro potřeby {@link validateValue}.
 * Je možné sem doregistrovat další validační funkce přes {@link registerCustomValidationFunction}.
 */
export const validationFnMap = new Map<CustomizableExplicitValidationFnKey, ExplicitValidationFunction>([
    [ExplicitValidationFnKey.maxLength, validateMaxLength],
    [ExplicitValidationFnKey.regex, validateRegex],
    [ExplicitValidationFnKey.minValue, validateMinValue],
    [ExplicitValidationFnKey.maxValue, validateMaxValue],
]);

/**
 * Vrací validační funkci se zadaným klíčem. Nebo vyhodí výjimku, pokud pro daný klíč funkce neexistuje.
 */
export const getValidationFn = (fnKey: CustomizableExplicitValidationFnKey): ExplicitValidationFunction => {
    const validationFn = validationFnMap.get(fnKey);
    invariant(!!validationFn, `[Controls/ExplicitValidation] Function for given key not found: ${fnKey}`);
    return validationFn;
};

/**
 * Zaregistruje zákaznickou validační funkci. Po zaregistrování bude možné tuto funkci předávat do {@link validateValue}.
 * @param fnKey Klíč identifikující danou funkci. Musí být jedinečný, jinak se registrace nezdaří.
 * @param customFunction Validační funkce k zaregistrování.
 */
export const registerCustomValidationFunction = (fnKey: CustomizableExplicitValidationFnKey, customFunction: ExplicitValidationFunction) => {
    if (validationFnMap.get(fnKey)) {
        throw new Error(`[Controls/ExplicitValidation] Function with given key already exists: ${fnKey}`);
    }
    validationFnMap.set(fnKey, customFunction);
};

/**
 * Zvaliduje zadanou hodnotu zadanými validačními funkcemi.
 * @param value Hodnota k validaci.
 * @param descriptions Seznam validací, které budou na `value` aplikovány.
 * @returns Seznam nalezených důvodů, proč `value` není validní. Nebo prázdný seznam, pokud validní je.
 */
export const validateValue = (value: ControlReduxRawValue, descriptions: ReadonlyArray<IExplicitValidationFnDescription>): ReadonlyArray<IExplicitValidationError> => (
    descriptions.reduce((errors, currentDescription) => {
        const validationFn = getValidationFn(currentDescription.fnKey);
        const error = validationFn(value, currentDescription.args, currentDescription.locKey);
        if (error) {
            errors.push(error);
        }
        return errors;
    }, [] as IExplicitValidationError[])
);

/**
 * Převádí `maxLength` na instanci {IExplicitValidationFnDescription} určenou pro {@link validateMaxLength}.
 * @param maxLength
 * @param locKey Pokud zadán, bude tento lokalizační klíč použit jako důvod nevalidnosti (viz `IExplicitValidationFnDescription.locKey`).
 * Jinak bude jako důvod nevalidnosti použit {@link MAX_LENGTH_ERROR_LOC_KEY}.
 */
export const createMaxLengthDescription = (maxLength: number, locKey?: string): IExplicitValidationFnDescription => ({
    fnKey: ExplicitValidationFnKey.maxLength,
    args: { maxLength },
    locKey: locKey ?? MAX_LENGTH_ERROR_LOC_KEY,
});

/**
 * Převádí `regex` na instanci {IExplicitValidationFnDescription} určenou pro {@link validateRegex}.
 * @param regex
 * @param locKey Pokud zadán, bude tento lokalizační klíč použit jako důvod nevalidnosti (viz `IExplicitValidationFnDescription.locKey`).
 * Jinak bude jako důvod nevalidnosti použit {@link INVALID_CONTROL_VALUE_LOC_KEY}.
 */
export const createRegexDescription = (regex: string, locKey?: string): IExplicitValidationFnDescription => ({
    fnKey: ExplicitValidationFnKey.regex,
    args: { regex },
    locKey: locKey ?? INVALID_CONTROL_VALUE_LOC_KEY,
});

/**
 * Vytvoří instanci {@link IExplicitValidationFnDescription} určenou pro {@link validateMinValue}.
 *
 * @param isBigNumber Příznak, zda se má validovat hodnota jako BigNumber.
 * @param minValue Dolní limit validní hodnoty (včetně).
 * @param locKey Pokud zadán, bude tento lokalizační klíč použit jako důvod nevalidnosti (viz `IExplicitValidationFnDescription.locKey`).
 * Jinak bude jako důvod nevalidnosti použit {@link INVALID_CONTROL_VALUE_LOC_KEY}.
 */
export const createMinValueDescription = (
    minValue: number,
    isBigNumber: boolean = false,
    locKey: string = MIN_VALUE_VALIDATION_ERROR_ARGS_LOC_KEY,
): IExplicitValidationFnDescription => ({
    fnKey: ExplicitValidationFnKey.minValue,
    args: {
        isBigNumber,
        minValue,
    },
    locKey,
});

/**
 * Vytvoří instanci {@link IExplicitValidationFnDescription} určenou pro {@link validateMaxValue}.
 *
 * @param isBigNumber Příznak, zda se má validovat hodnota jako BigNumber.
 * @param maxValue Horní limit validní hodnoty (včetně).
 * @param locKey Pokud zadán, bude tento lokalizační klíč použit jako důvod nevalidnosti (viz `IExplicitValidationFnDescription.locKey`).
 * Jinak bude jako důvod nevalidnosti použit {@link INVALID_CONTROL_VALUE_LOC_KEY}.
 */
export const createMaxValueDescription = (
    maxValue: number,
    isBigNumber: boolean = false,
    locKey: string = MAX_VALUE_VALIDATION_ERROR_ARGS_LOC_KEY,
): IExplicitValidationFnDescription => ({
    fnKey: ExplicitValidationFnKey.maxValue,
    args: {
        isBigNumber,
        maxValue,
    },
    locKey,
});

/**
 * Vrátí novou instanci {@link IExplicitValidationError} se zadanými `locKey` a `locArgs`.
 * @param locKey Lokalizační klíč. Např. "GUI-123".
 * @param locArgs Parametry, které budou dosazeny za zástupné znaky v lokalizovaném textu.
 */
export const createValidationError = (locKey: string, locArgs?: string[]): IExplicitValidationError => ({
    locKey,
    locArgs,
});

/**
 * Vrátí novou instanci {@link ILocalizedExplicitValidationError}.
 * @param message Lokalizovaná zpráva určená k přímému zobrazení klientovi.
 */
export const createLocalizedValidationError = (message: string): ILocalizedExplicitValidationError => ({
    message,
});

export const ExplicitValidation = {
    createMaxLengthDescription,
    createRegexDescription,
    registerCustomValidationFunction,
    validateValue,
    createValidationError,
    createLocalizedValidationError,
    createMinValueDescription,
    createMaxValueDescription,
};
